// src/pages/UserManagement.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Typography,
  Paper,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  Dialog,
  Grid,
  Avatar,
  Chip,
  Divider,
  Stack,
  Card,
  CardActions,
  Snackbar,
  Alert,
  Switch,
  FormControlLabel, Autocomplete, Checkbox, ListItemText,  ToggleButton,  ToggleButtonGroup, 
  DialogTitle, DialogContent, DialogActions, FormHelperText
} from "@mui/material";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import TableRowsIcon from "@mui/icons-material/TableRows";

import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  updateDoc,
  doc,
  serverTimestamp,
  query,
  where,
  setDoc,
} from "firebase/firestore";

import {
  getAuth,
  createUserWithEmailAndPassword, // secondary app creation
} from "firebase/auth";

import { initializeApp, getApps, deleteApp } from "firebase/app";

import { db, app } from "../firebase/firebaseConfig";
import { useAuth } from "../authContext";
import { logActivity } from "../utils/activityLogger";
import { useNavigate } from "react-router-dom";

import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";

import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import { getFunctions, httpsCallable } from "firebase/functions";


/* ---------- helpers ---------- */

const CATEGORY_OPTIONS = [
  "Leadership (owners, managing partners)",
  "Engagement Heads (Experienced Professionals)",
  "Qualified Professionals (CA, CS, ACCA or Other Post Graduate qualifications)",
  "Semi-Qualified Staff (pursuing professional course)",
  "Trainee’s (Interns etc)",
  "Support Staff (other employees)",
  "HR (internal administrative staff)",
];

const stringToColor = (str = "") => {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = "#";
  for (let i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
};

const initials = (name = "") => {
  const parts = name.trim().split(/\s+/);
  if (!parts.length) return "U";
  return (parts[0][0] || "").toUpperCase() + (parts[1]?.[0] || "").toUpperCase();
};

const roleChipColor = (role) =>
  role === "Admin" ? "error" : role === "Manager" ? "primary" : "success";

/* Small presentational card for each user */
/* Small presentational card for each user (shows all table fields) */
const UserCard = ({ u, onEdit, onDelete, onAddTask }) => {
  const active = (u.isActive ?? true) === true;

  const Meta = ({ icon, children, title }) => (
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
      <Box sx={{ color: "text.secondary", display: "flex", alignItems: "center" }}>{icon}</Box>
      <Typography
        variant="body2"
        sx={{ minWidth: 0 }}
        noWrap
        title={title || (typeof children === "string" ? children : undefined)}
      >
        {children || "—"}
      </Typography>
    </Stack>
  );

  return (
    <Card
      sx={{
        p: 2.25,
        borderRadius: 3,
        height: "100%",
        boxShadow: 2,
        transition: "all .18s ease",
        "&:hover": { boxShadow: 4, transform: "translateY(-2px)" },
      }}
    >
      <Grid container spacing={2}>
        {/* Avatar */}
        <Grid item xs={12} sm={3} md={3} sx={{ display: "flex", justifyContent: "center" }}>
          <Avatar
            sx={{
              width: 90,
              height: 90,
              bgcolor: stringToColor(u.name || u.email || "User"),
              fontSize: 28,
              boxShadow: 2,
            }}
          >
            {initials(u.name || u.email)}
          </Avatar>
        </Grid>

        {/* Content */}
        <Grid item xs={12} sm={9} md={9} sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          {/* Header: Name + Role/Status */}
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, minWidth: 0 }}
              noWrap
              title={u.name || "Unnamed"}
            >
              {u.name || "Unnamed"}
            </Typography>

            <Chip
              size="small"
              label={active ? "Active" : "Inactive"}
              color={active ? "success" : "default"}
              variant={active ? "filled" : "outlined"}
            />
</Stack>
            {!!u.category && <Chip size="small" label={u.category} variant="outlined"/>}
          

          {/* Meta rows: Email, Company, Category (again, as text), Role (if you want it here too) */}
          <Grid container spacing={1.2} sx={{ mt: 1.25 }}>
            <Grid item xs={12} sm={6} md={6}>
              <Meta icon={<EmailOutlinedIcon fontSize="small" />} title={u.email}>
                <a
                  href={`mailto:${u.email}`}
                  style={{ color: "inherit", textDecoration: "none" }}
                >
                  {u.email}
                </a>
              </Meta>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <Meta icon={<WorkOutlineIcon fontSize="small" />} title={u.companyName}>
                {u.companyName || "—"}
              </Meta>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <Meta icon={<LocalOfferOutlinedIcon fontSize="small" />} title={u.category}>
                {u.category || "—"}
              </Meta>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <Meta icon={<WorkOutlineIcon fontSize="small" />}>
                <Chip
                  size="small"
                  label={u.role || "—"}
                  color={u.role ? roleChipColor(u.role) : "default"}
                  variant={u.role ? "filled" : "outlined"}
                />
              </Meta>
            </Grid>
          </Grid>

          {/* Actions */}
          <CardActions sx={{ px: 0, pt: 1.5 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AssignmentOutlinedIcon />}
              onClick={() => onAddTask(u)}
            >
              Add Task
            </Button>

            <Box sx={{ ml: "auto" }}>
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => onEdit(u)}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton size="small" color="error" onClick={() => onDelete(u)}>
                  <DeleteOutlineOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </CardActions>
        </Grid>
      </Grid>
    </Card>
  );
};


export default function UserManagement() {
  const navigate = useNavigate();
  const { companyId, companyName, user } = useAuth();

  const gotoAddTaskFor = (u) => {
    const userName = u?.name || "";
    navigate(`/tasks?user=${encodeURIComponent(userName)}&open=add`);
  };

  /* ---------- data ---------- */
  const [users, setUsers] = useState([]);

  /* ---------- filters ---------- */
  // Multi-select filters (like Category)
const [filterNames, setFilterNames] = useState([]);   // array of names (strings)
const [filterEmails, setFilterEmails] = useState([]); // array of emails (strings)
  const [filterRole, setFilterRole] = useState("");

  /* ---------- sorting ---------- */
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");

  /* ---------- pagination ---------- */
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  /* ---------- dialogs ---------- */
  const [openDialog, setOpenDialog] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  

// NEW: chip filters
const [filterStatus, setFilterStatus] = useState("");     // "", "Active", "Inactive"
const [filterCategories, setFilterCategories] = useState([]); // array of strings


  const [currentUser, setCurrentUser] = useState({
    id: null,
    uid: "",
    name: "",
    role: "",
    email: "",
    password: "", // only used on create
    companyId: "",
    companyName: "",
    // NEW FIELDS:
    isActive: true,
    category: "",
  });

  // save state + snackbar
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    severity: "success",
    msg: "",
  });

  /* ---------- view toggle ---------- */
  const [viewMode, setViewMode] = useState("cards");

const functions = getFunctions(undefined, "us-central1"); // <-- use your deployed region
const setUserPasswordFn = httpsCallable(functions, "adminSetUserPassword");

const [pwdDlgOpen, setPwdDlgOpen] = React.useState(false);
const [pwdFor, setPwdFor] = React.useState(null);     // the user row object
const [pwd1, setPwd1] = React.useState("");
const [pwd2, setPwd2] = React.useState("");
const [pwdBusy, setPwdBusy] = React.useState(false);

const openSetPassword = (u) => {
  setPwdFor(u);
  setPwd1("");
  setPwd2("");
  setPwdDlgOpen(true);
};
const closeSetPassword = () => setPwdDlgOpen(false);

const submitSetPassword = async () => {
  const targetUid = pwdFor?.uid || pwdFor?.id || null; // prefer auth UID
  const targetEmail = pwdFor?.email || "";
  await setUserPasswordFn({ targetUid, targetEmail, newPassword: pwd1 });

  if (pwd1.length < 6) {
    setSnack({ open: true, severity: "error", msg: "Password must be at least 6 characters." });
    return;
  }
  if (pwd1 !== pwd2) {
    setSnack({ open: true, severity: "error", msg: "Passwords do not match." });
    return;
  }
  try {
    setPwdBusy(true);
    await setUserPasswordFn({ targetUid, targetEmail, newPassword: pwd1 });
    setSnack({ open: true, severity: "success", msg: `Password updated for ${pwdFor.email}` });
    // optional extra activity log if you want client-side too
    await logActivity({
      userId: user?.uid,
      userName: user?.name || user?.email || "Unknown",
      companyId,
      action: "ADMIN_SET_PASSWORD",
      module: "User",
      details: `Set password for "${pwdFor.email}".`,
    });
    setPwdDlgOpen(false);
  } catch (e) {
    console.error(e);
    setSnack({
     open: true,
     severity: "error",
     msg: `${e?.code || "error"}: ${e?.message || "Failed to set password."}`,
   });
  } finally {
    setPwdBusy(false);
  }
};




  /* ---------- fetch users ---------- */
  const fetchUsers = async () => {
    try {
      if (!companyId) return;
      const usersQuery = query(collection(db, "users"), where("companyId", "==", companyId));
      const snap = await getDocs(usersQuery);
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setUsers(list);
    } catch (e) {
      console.error("Error fetching users:", e);
    }
  };

  useEffect(() => {
    fetchUsers();
    // activity log: view page
    logActivity({
      userId: user?.uid,
      userName: user?.name || user?.email || "Unknown",
      companyId,
      action: "VIEW",
      module: "User",
      details: "Visited Users page",
    }).catch(() => {});
  }, [companyId]); // eslint-disable-line

  /* ---------- filter + sort + page pipeline ---------- */
  const filtered = useMemo(() => {
 const selectedNames  = filterNames;
 const selectedEmails = filterEmails;
    const r = filterRole.trim();
     const s = filterStatus;        // "", "Active", "Inactive"
  
  const cats = filterCategories; // [] or list of categories

    return users.filter((u) => {
   const okName  = selectedNames.length
     ? selectedNames.includes((u.name || "").trim())
     : true;
   const okEmail = selectedEmails.length
     ? selectedEmails.includes((u.email || "").trim())
     : true;
      const okRole = r ? (u.role || "") === r : true;

      // NEW: status/category filters
    const isActive = (u.isActive ?? true) === true;
    const okStatus = s ? (s === "Active" ? isActive : !isActive) : true;
    

    const okCategory = cats.length
     ? cats.includes(u.category || "")
     : true;

    return okName && okEmail && okRole && okStatus && okCategory;
  });
}, [users, filterNames, filterEmails, filterRole, filterStatus, filterCategories]);


// Options for Name / Email dropdowns, derived from current users
const nameOptions = useMemo(() => {
  const set = new Set(
    users.map(u => (u.name || "").trim()).filter(Boolean)
  );
  return Array.from(set).sort();
}, [users]);

const emailOptions = useMemo(() => {
  const set = new Set(
    users.map(u => (u.email || "").trim()).filter(Boolean)
  );
  return Array.from(set).sort();
}, [users]);


  const sorted = useMemo(() => {
    const toVal = (val) => {
      if (typeof val === "boolean") return val ? "a" : "b"; // Active before Inactive
      return (val ?? "").toString().toLowerCase();
    };
    const sortedCopy = [...filtered].sort((a, b) => {
      const va = toVal(a?.[sortField]);
      const vb = toVal(b?.[sortField]);
      return va.localeCompare(vb);
    });
    return sortOrder === "asc" ? sortedCopy : sortedCopy.reverse();
  }, [filtered, sortField, sortOrder]);

  const totalRows = sorted.length;
  const pageCount = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const startIndex = Math.min((page - 1) * rowsPerPage, Math.max(0, totalRows - 1));
  const endIndex = Math.min(startIndex + rowsPerPage, totalRows);
  const paged = useMemo(() => sorted.slice(startIndex, startIndex + rowsPerPage), [sorted, startIndex, rowsPerPage]);

  const handleSort = (field) => {
    setSortOrder((prev) => (field === sortField ? (prev === "asc" ? "desc" : "asc") : "asc"));
    setSortField(field);
  };
  const arrow = (field) => (sortField === field ? (sortOrder === "asc" ? " ↑" : " ↓") : "");

  const clearFilters = () => {
    setFilterNames("");
    setFilterEmails("");
    setFilterRole("");
    // NEW:
  setFilterStatus("");
  setFilterCategories("");
    setPage(1);
  };

  const onRequestDeleteUser = (u) => {
    setPendingDelete(u);
    setConfirmOpen(true);
  };

  const onConfirmDeleteUser = async () => {
    try {
      if (pendingDelete?.id) {
        await deleteDoc(doc(db, "users", pendingDelete.id));
        await logActivity({
          userId: user?.uid,
          userName: user?.name || user?.email || "Unknown",
          companyId,
          action: "DELETE",
          module: "User",
          details: `Deleted user "${pendingDelete.name || pendingDelete.email}"`,
        });
        await fetchUsers();
      }
    } catch (e) {
      console.error("Delete failed:", e);
      alert("Failed to delete user.");
    } finally {
      setConfirmOpen(false);
      setPendingDelete(null);
    }
  };

  const auth = getAuth(); // primary (admin) session

  const handleOpenDialog = async (u = null) => {
    let effectiveCompanyId = companyId;
    let effectiveCompanyName = companyName;

    // Best effort fallback from current auth user's profile
    try {
      if (!effectiveCompanyId || !effectiveCompanyName) {
        const snap = await getDocs(query(collection(db, "users"), where("uid", "==", auth.currentUser?.uid || "")));
        if (!snap.empty) {
          const ud = snap.docs[0].data() || {};
          effectiveCompanyId = ud.companyId || effectiveCompanyId;
          effectiveCompanyName = ud.companyName || effectiveCompanyName;
        }
      }
    } catch (err) {
      console.error("Error resolving company info:", err);
    }

    if (!effectiveCompanyId || !effectiveCompanyName) {
      alert("Company ID/Name missing. Try again in a few seconds.");
      return;
    }

    setCurrentUser(
      u
        ? {
            ...u,
            password: "",
            isActive: u.isActive ?? true,
            category: u.category ?? "",
          }
        : {
            id: null,
            uid: "",
            name: "",
            role: "",
            email: "",
            password: "",
            companyId: effectiveCompanyId,
            companyName: effectiveCompanyName,
            isActive: true,
            category: "",
          }
    );
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentUser({
      id: null,
      uid: "",
      name: "",
      role: "",
      email: "",
      password: "",
      companyId: "",
      companyName: "",
      isActive: true,
      category: "",
    });
  };

  // CREATE/UPDATE user
  const mapAuthError = (err) => {
    const code = err?.code || "";
    switch (code) {
      case "auth/email-already-in-use":
        return "That email is already registered.";
      case "auth/invalid-email":
        return "Invalid email address.";
      case "auth/weak-password":
        return "Password should be at least 6 characters.";
      case "auth/operation-not-allowed":
        return "Email/Password sign-in is disabled for this Firebase project.";
      default:
        return err?.message || "Failed to save user.";
    }
  };

  const handleSaveUser = async () => {
    if (!currentUser.name || !currentUser.role || !currentUser.email) {
      alert("Please fill Name, Role and Email.");
      return;
    }
    if (!currentUser.companyId || !currentUser.companyName) {
      alert("Company info missing.");
      return;
    }

    setSaving(true);
    try {
      if (currentUser.id) {
        // UPDATE (no password change here)
        await updateDoc(doc(db, "users", currentUser.id), {
          uid: currentUser.uid || "",
          name: currentUser.name,
          role: currentUser.role,
          email: currentUser.email,
          isActive: currentUser.isActive ?? true,
          category: currentUser.category ?? "",
          updatedAt: serverTimestamp(),
        });
        await logActivity({
          userId: user?.uid,
          userName: user?.name || user?.email || "Unknown",
          companyId,
          action: "UPDATE",
          module: "User",
          details: `Updated user "${currentUser.name || currentUser.email}"`,
        });
      } else {
        // CREATE
        if (!currentUser.password || currentUser.password.trim().length < 6) {
          alert("Please enter a password (min 6 characters).");
          return;
        }

        // prevent duplicate by email in your UI list
        const exists = users.some(
          (u) => (u.email || "").toLowerCase() === currentUser.email.trim().toLowerCase()
        );
        if (exists) {
          alert("That email already exists in your users list.");
          return;
        }

        // spin up secondary app so the admin session stays intact
        let secondary;
        let createdSecondary = false;
        try {
          secondary = getApps().find((a) => a.name === "secondary");
          if (!secondary) {
            secondary = initializeApp(app.options, "secondary");
            createdSecondary = true;
          }
          const secondaryAuth = getAuth(secondary);

          // 1) create auth user
          const cred = await createUserWithEmailAndPassword(
            secondaryAuth,
            currentUser.email.trim(),
            currentUser.password.trim()
          );

          // 2) create firestore profile (doc id == uid)
          await setDoc(doc(db, "users", cred.user.uid), {
            uid: cred.user.uid,
            name: currentUser.name,
            role: currentUser.role,
            email: currentUser.email.trim(),
            companyId: currentUser.companyId,
            companyName: currentUser.companyName,
            isActive: currentUser.isActive ?? true,
            category: currentUser.category ?? "",
            createdAt: serverTimestamp(),
          });

          await logActivity({
            userId: user?.uid,
            userName: user?.name || user?.email || "Unknown",
            companyId,
            action: "CREATE",
            module: "User",
            details: `Created user "${currentUser.name || currentUser.email}"`,
          });
        } finally {
          // always try to clean up the secondary app
          try {
            if (secondary && createdSecondary) await deleteApp(secondary);
          } catch {}
        }
      }

      await fetchUsers();
      setSnack({
        open: true,
        severity: "success",
        msg: currentUser.id ? "User updated" : "User added",
      });
      handleCloseDialog();
    } catch (e) {
      console.error("Save failed:", e);
      setSnack({ open: true, severity: "error", msg: mapAuthError(e) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container disableGutters maxWidth={false} sx={{ py: 2 }}>
      <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 } }} />

      <Typography variant="h5" gutterBottom>
        Users
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      flexWrap: "wrap",
    }}
  >
   {(() => {
  const ALL = { label: "Select all", value: "__ALL__" };
  const optionObjs = nameOptions.map(n => ({ label: n, value: n }));
  const allSelected = filterNames.length === nameOptions.length;
  const valueObjs = allSelected
    ? [ALL, ...optionObjs]
    : optionObjs.filter(o => filterNames.includes(o.value));

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      options={[ALL, ...optionObjs]}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(o, v) => o.value === v.value}
      value={valueObjs}
      onChange={(_, newValue) => {
        const hasAll = newValue.some(o => o.value === "__ALL__");
        if (hasAll) {
          setFilterNames(allSelected ? [] : [...nameOptions]);
        } else {
          setFilterNames(newValue.map(o => o.value));
        }
        setPage(1);
      }}
      renderOption={(props, option) => {
        const checked =
          option.value === "__ALL__"
            ? allSelected
            : filterNames.includes(option.value);
        return (
          <li {...props} key={option.value}>
            <Checkbox size="small" checked={checked} />
            <ListItemText primary={option.label} />
          </li>
        );
      }}
      renderTags={(selected) =>
        selected.length ? (
          <Chip size="small" label={`${allSelected ? "All" : selected.length} selected`} />
        ) : null
      }
      renderInput={(params) => (
        <TextField {...params} size="small" label="Name" placeholder="Search names…" />
      )}
      sx={{ width: 200, flex: "0 0 auto" }}
    />
  );
})()}


{(() => {
  const ALL = { label: "Select all", value: "__ALL__" };
  const optionObjs = emailOptions.map(e => ({ label: e, value: e }));
  const allSelected = filterEmails.length === emailOptions.length;
  const valueObjs = allSelected
    ? [ALL, ...optionObjs]
    : optionObjs.filter(o => filterEmails.includes(o.value));

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      options={[ALL, ...optionObjs]}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(o, v) => o.value === v.value}
      value={valueObjs}
      onChange={(_, newValue) => {
        const hasAll = newValue.some(o => o.value === "__ALL__");
        if (hasAll) {
          setFilterEmails(allSelected ? [] : [...emailOptions]);
        } else {
          setFilterEmails(newValue.map(o => o.value));
        }
        setPage(1);
      }}
      renderOption={(props, option) => {
        const checked =
          option.value === "__ALL__"
            ? allSelected
            : filterEmails.includes(option.value);
        return (
          <li {...props} key={option.value}>
            <Checkbox size="small" checked={checked} />
            <ListItemText primary={option.label} />
          </li>
        );
      }}
      renderTags={(selected) =>
        selected.length ? (
          <Chip size="small" label={`${allSelected ? "All" : selected.length} selected`} />
        ) : null
      }
      renderInput={(params) => (
        <TextField {...params} size="small" label="Email" placeholder="Search emails…" />
      )}
      sx={{ width: 240, flex: "0 0 auto" }}
    />
  );
})()}


    {/* Role */}
    <FormControl size="small" sx={{ width: { xs: "100%", sm: 160 } }}>
      <InputLabel>Role</InputLabel>
      <Select label="Role" value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
        <MenuItem value="">All</MenuItem>
        <MenuItem value="Admin">Admin</MenuItem>
        <MenuItem value="Manager">Manager</MenuItem>
        <MenuItem value="Member">Member</MenuItem>
      </Select>
    </FormControl>

    {/* Category: searchable multi-select w/ checkboxes + "Select all" */}
    {(() => {
      const ALL = { label: "Select all", value: "__ALL__" };
      const optionObjs = CATEGORY_OPTIONS.map((c) => ({ label: c, value: c }));
      const allSelected = filterCategories.length === CATEGORY_OPTIONS.length;
      const valueObjs = allSelected
        ? [ALL, ...optionObjs]
        : optionObjs.filter((o) => filterCategories.includes(o.value));

      return (
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={[ALL, ...optionObjs]}
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(o, v) => o.value === v.value}
          value={valueObjs}
          onChange={(_, newValue) => {
            const hasAll = newValue.some((o) => o.value === "__ALL__");
            if (hasAll) {
              setFilterCategories(allSelected ? [] : [...CATEGORY_OPTIONS]);
            } else {
              setFilterCategories(newValue.map((o) => o.value));
            }
            setPage(1);
          }}
          renderOption={(props, option) => {
            const checked =
              option.value === "__ALL__"
                ? allSelected
                : filterCategories.includes(option.value);
            return (
              <li {...props} key={option.value}>
                <Checkbox size="small" checked={checked} />
                <ListItemText primary={option.label} />
              </li>
            );
          }}
          renderTags={(selected) =>
            selected.length ? (
              <Chip size="small" label={`${allSelected ? "All" : selected.length} selected`} />
            ) : null
          }
          renderInput={(params) => (
            <TextField {...params} size="small" label="Category" placeholder="Search categories…" />
          )}
          sx={{ width: { xs: "100%", sm: 200 , md: 200 } }}
        />
      );
    })()}

    {/* Status toggle (All / Active / Inactive) */}
    <ToggleButtonGroup
      exclusive
      size="small"
      value={filterStatus}
      onChange={(_, v) => setFilterStatus(v ?? "")}
      sx={{ ml: { md: 0 }, borderRadius: 2 }}
    >
      <ToggleButton value="" aria-label="All">All</ToggleButton>
      <ToggleButton value="Active" aria-label="Active">Active</ToggleButton>
      <ToggleButton value="Inactive" aria-label="Inactive">Inactive</ToggleButton>
    </ToggleButtonGroup>

    {/* Spacer pushes buttons to the right */}
    <Box sx={{ flex: 1 }} />

    {/* View / Clear / Add buttons (unchanged) */}
    <Stack direction="row" spacing={1} alignItems="center">
      <Button
        size="small"
        startIcon={<ViewModuleIcon />}
        variant={viewMode === "cards" ? "contained" : "outlined"}
        onClick={() => setViewMode("cards")}
      >
        Cards
      </Button>
      <Button
        size="small"
        startIcon={<TableRowsIcon />}
        variant={viewMode === "table" ? "contained" : "outlined"}
        onClick={() => setViewMode("table")}
      >
        Table
      </Button>
      <Button variant="outlined" size="small" onClick={clearFilters}>
        Clear Filters
      </Button>
      <Button variant="contained" onClick={() => handleOpenDialog(null)}>
        Add User
      </Button>
    </Stack>
  </Box>


  
</Paper>

      

      {/* Cards or Table */}
      {viewMode === "cards" ? (
        <Paper sx={{ p: 2 }}>
          <Grid container spacing={2}>
            {paged.map((u) => (
              <Grid item xs={12} sm={6} md={4} lg={4} key={u.id}>
                <UserCard u={u} onEdit={handleOpenDialog} onDelete={onRequestDeleteUser} onAddTask={gotoAddTaskFor} />
              </Grid>
            ))}
            {paged.length === 0 && (
              <Grid item xs={12}>
                <Box sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>No users found.</Box>
              </Grid>
            )}
          </Grid>

          <Box sx={{ mt: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="body2">
              Showing <strong>{totalRows === 0 ? 0 : startIndex + 1}</strong> to <strong>{endIndex}</strong> of{" "}
              <strong>{totalRows}</strong> entries
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="outlined" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                Previous
              </Button>
              <Button
                variant="outlined"
                disabled={page >= pageCount}
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              >
                Next
              </Button>
              <FormControl size="small" sx={{ ml: 2, minWidth: 100 }}>
                <InputLabel>Show</InputLabel>
                <Select
                  label="Show"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  {[5, 10, 25, 50].map((n) => (
                    <MenuItem key={n} value={n}>
                      {n}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Paper>
      ) : (
        <Paper>
          <TableContainer sx={{ maxHeight: 560, overflow: "auto" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell onClick={() => handleSort("companyName")} sx={{ cursor: "pointer" }}>
                    Company{arrow("companyName")}
                  </TableCell>
                  <TableCell onClick={() => handleSort("name")} sx={{ cursor: "pointer" }}>
                    Name{arrow("name")}
                  </TableCell>
                  
                  {/* Existing */}
                  <TableCell onClick={() => handleSort("email")} sx={{ cursor: "pointer" }}>
                    Email{arrow("email")}
                  </TableCell>
                  
                  <TableCell onClick={() => handleSort("category")} sx={{ cursor: "pointer" }}>
                    Category{arrow("category")}
                  </TableCell>
                  
                  <TableCell onClick={() => handleSort("role")} sx={{ cursor: "pointer" }}>
                    Role{arrow("role")}
                  </TableCell>
                  {/* New columns */}
                  <TableCell onClick={() => handleSort("isActive")} sx={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                    Status{arrow("isActive")}
                  </TableCell>
                  
                  <TableCell>Actions</TableCell>

                  <TableCell>Reset Password</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paged.map((u) => (
                  <TableRow key={u.id}>
                    
                    <TableCell>{u.companyName}</TableCell>
                    <TableCell>{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    
                    <TableCell>{u.category || "—"}</TableCell>
                    <TableCell>{u.role}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={(u.isActive ?? true) ? "Active" : "Inactive"}
                        color={(u.isActive ?? true) ? "success" : "default"}
                        variant={(u.isActive ?? true) ? "filled" : "outlined"}
                      />
                    </TableCell>
                    
                    <TableCell sx={{ minWidth: 90 }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleOpenDialog(u)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => onRequestDeleteUser(u)}>
                          <DeleteOutlineOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                    <Tooltip title="Set new password (admin)">
                      <IconButton size="small" onClick={() => openSetPassword(u)}>
                      <KeyOutlinedIcon fontSize="small" />
                    </IconButton>
                    </Tooltip>
                  </TableCell>


                  </TableRow>
                ))}

                {paged.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No users found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="body2">
              Showing <strong>{totalRows === 0 ? 0 : startIndex + 1}</strong> to <strong>{endIndex}</strong> of{" "}
              <strong>{totalRows}</strong> entries
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Button variant="outlined" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                Previous
              </Button>
              <Button
                variant="outlined"
                disabled={page >= pageCount}
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              >
                Next
              </Button>
              <FormControl size="small" sx={{ ml: 2, minWidth: 100 }}>
                <InputLabel>Show</InputLabel>
                <Select
                  label="Show"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  {[5, 10, 25, 50].map((n) => (
                    <MenuItem key={n} value={n}>
                      {n}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <Typography component="div" variant="h6" sx={{ px: 3, pt: 2 }}>
          {currentUser.id ? "Edit User" : "Add User"}
        </Typography>
        <Box sx={{ px: 3, pb: 2, pt: 1, minWidth: 420 }}>
          <TextField
            label="Name"
            fullWidth
            size="small"
            sx={{ mt: 2 }}
            value={currentUser.name}
            onChange={(e) => setCurrentUser({ ...currentUser, name: e.target.value })}
          />
          <TextField
            label="Email"
            fullWidth
            size="small"
            sx={{ mt: 2 }}
            value={currentUser.email}
            onChange={(e) => setCurrentUser({ ...currentUser, email: e.target.value })}
          />

          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel>Role</InputLabel>
            <Select
              label="Role"
              value={currentUser.role}
              onChange={(e) => setCurrentUser({ ...currentUser, role: e.target.value })}
            >
              <MenuItem value="Admin">Admin</MenuItem>
              <MenuItem value="Manager">Manager</MenuItem>
              <MenuItem value="Member">Member</MenuItem>
            </Select>
          </FormControl>

          {/* New: Active toggle */}
          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Switch
                checked={currentUser.isActive ?? true}
                onChange={(e) => setCurrentUser({ ...currentUser, isActive: e.target.checked })}
              />
            }
            label={(currentUser.isActive ?? true) ? "Active" : "Inactive"}
          />

          {/* New: Category select */}
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel>Category</InputLabel>
            <Select
              label="Category"
              value={currentUser.category}
              onChange={(e) => setCurrentUser({ ...currentUser, category: e.target.value })}
            >
              <MenuItem value="">—</MenuItem>
              {CATEGORY_OPTIONS.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {!currentUser.id && (
            <TextField
              label="Password (min 6 chars)"
              fullWidth
              size="small"
              type="password"
              sx={{ mt: 2 }}
              value={currentUser.password}
              onChange={(e) => setCurrentUser({ ...currentUser, password: e.target.value })}
              autoComplete="new-password"
            />
          )}
        </Box>

        <Box sx={{ px: 3, pb: 2, display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveUser} variant="contained" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Dialog>

      {/* Confirm Delete dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <Typography component="div" variant="h6" sx={{ px: 3, pt: 2 }}>
          Delete user?
        </Typography>
        <Box sx={{ px: 3, pb: 2, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {pendingDelete ? (
              <>
                This will permanently remove <strong>{pendingDelete.name || pendingDelete.email || "this user"}</strong>.
              </>
            ) : (
              "This will permanently remove the user."
            )}
          </Typography>
        </Box>
        <Box sx={{ px: 3, pb: 2, display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={onConfirmDeleteUser}>
            Delete
          </Button>
        </Box>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={2500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>

<Dialog open={pwdDlgOpen} onClose={pwdBusy ? undefined : closeSetPassword} maxWidth="xs" fullWidth>
  <DialogTitle>Set Password</DialogTitle>
  <DialogContent>
    <Typography variant="body2" sx={{ mb: 1 }}>
      {pwdFor?.email}
    </Typography>
    <TextField
      label="New password"
      type="password"
      fullWidth
      margin="dense"
      value={pwd1}
      onChange={(e) => setPwd1(e.target.value)}
      autoFocus
    />
    <TextField
      label="Confirm password"
      type="password"
      fullWidth
      margin="dense"
      value={pwd2}
      onChange={(e) => setPwd2(e.target.value)}
    />
    <FormHelperText>Password can be reset by Admin only. Password must be at least 6 characters.</FormHelperText>
  </DialogContent>
  <DialogActions>
    <Button onClick={closeSetPassword} disabled={pwdBusy}>Cancel</Button>
    <Button variant="contained" onClick={submitSetPassword} disabled={pwdBusy}>
      {pwdBusy ? "Saving..." : "Save"}
    </Button>
  </DialogActions>
</Dialog>


    </Container>
  );
}
